import {
  type ColumnDef,
  useReactTable,
  getCoreRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  getFilteredRowModel,
  type VisibilityState,
  type ColumnFiltersState,
} from "@tanstack/react-table";
import React, { useCallback, useEffect, useState } from "react";
import Spinner from "../../../components/spinner.components";
import { DataTableComponents } from "../../../components/tables/table.components";
import type { IPagination } from "../../../types/models/pagination.models.types";
import { useToast } from "../../../hooks/use-toast";
import { debounce, handleHttpError } from "../../../utils/utils";
import SearchIcon from "../../../assets/svgs/common/search.svg";
import { Input } from "../../../components/ui/input";
import { XIcon, Youtube } from "lucide-react";
import { useGetSocialsAdminHttp } from "../../../hooks/http/admin/socials/get-socials.admin.http";
import useUserStore from "../../../store/socials.store";
import {
  SocialPlatforms,
  type ISocial,
} from "../../../types/models/social.models.types";
import { InstagramIcon } from "../../../assets/svgs/common/instagram.svg";
import GrowthIcon from "../../../assets/svgs/common/growth.svg";
import millify from "millify";
import PenIcon from "../../../assets/svgs/common/pen.svg";
import { Link } from "react-router-dom";
import { useSearchSocialsAdminHttp } from "../../../hooks/http/admin/socials/search-socials.admin.http";
import { RemoveFromFixedPayout } from "../../../components/action-buttons/fixed-payouts/remove-from-fixed-payouts-button.components";
import { Button } from "../../../components/ui/button";

const FixedPayoutsAdminPartial: React.FunctionComponent = () => {
  const [columnFilters, setColumnFilters] = React.useState<ColumnFiltersState>(
    [],
  );
  const [columnVisibility, setColumnVisibility] =
    React.useState<VisibilityState>({});
  const [rowSelection, setRowSelection] = React.useState({});
  const [sortBy, setSortBy] = React.useState("createdAt");

  const { data, setData, removeById } = useUserStore();
  const [, setPageNo] = React.useState(1);
  const { toast } = useToast();
  const [paginationData, setPaginationData] = React.useState<IPagination>();
  const [search, setSearch] = useState("");
  const {
    execute: searchSocialHttp,
    fetching: fetchingSocialsSearch,
    cancel: cancelSearchSocialHttp,
  } = useSearchSocialsAdminHttp();
  const [typing, setTyping] = useState(false);
  const {
    execute: getSocialsHttp,
    fetching: fetchingSocials,
    cancel: cancelGetSocialsHttp,
  } = useGetSocialsAdminHttp();

  async function getSocials(data: ISocial[], pageNo: number) {
    cancelGetSocialsHttp();
    const response = await getSocialsHttp({
      body: null,
      queryParams: {
        page: pageNo,
        size: 7,
        sortOrder: "desc",
        filterFixedPayout: true,
        sortBy: sortBy,
      },
    });
    if (!response.success) {
      return handleHttpError(response, toast);
    }
    const newSocials = response.data.socials.filter((social) => {
      return data.findIndex((i) => i.id === social.id) === -1;
    });
    setData([...data, ...newSocials]);
    setPaginationData(response.data.pagination);
  }

  const debouncedSearch = useCallback(
    debounce(async (searchValue: string) => {
      const searchString = searchValue.trim();
      if (!searchString) {
        return;
      }
      const response = await searchSocialHttp({
        body: null,
        queryParams: {
          searchString: searchString,
        },
      });
      if (!response.success) {
        return handleHttpError(response, toast);
      }

      setData(response.data.socials);
      setTyping(false);
    }, 500),
    [],
  );

  function handleSearch(e: React.ChangeEvent<HTMLInputElement>) {
    cancelSearchSocialHttp();

    setSearch(e.target.value);
    debouncedSearch(e.target.value);
    setTyping(true);
    if (!e.target.value.trim().length) {
      setData([]);
      getSocials([], 1);
      setTyping(false);
    }
  }

  // biome-ignore lint/correctness/useExhaustiveDependencies: <explanation>
  useEffect(() => {
    setData([]);
    getSocials([], 1);
  }, [sortBy]);

  const columns: ColumnDef<ISocial>[] = [
    {
      accessorKey: "handle",
      header: "User name",
      cell: ({ row }) => {
        const { handle, platform, youtube } = row.original;

        if (platform === SocialPlatforms.youtube) {
          const channelHandle = youtube?.channelHandle || handle;
          return (
            <div className="flex items-center gap-3 font-semibold tracking-wider">
              <div className="flex justify-center items-center border-2 rounded-full w-8 h-8 overflow-hidden">
                <img
                  className="rounded-full w-8 h-8 cover"
                  src={youtube?.profileImage}
                  alt=""
                />
              </div>
              <a
                href={`https://youtube.com/${channelHandle}/`}
                target="_blank"
                className="underline"
                aria-label="YouTube channel link"
                rel="noreferrer"
              >
                {channelHandle}
              </a>
            </div>
          );
        }

        // Instagram default
        return (
          <div className="flex items-center gap-3 font-semibold tracking-wider">
            <div className="border-2 rounded-full w-8 h-8" />
            <a
              href={`https://instagram.com/${handle}/`}
              target="_blank"
              className="underline"
              aria-label="Instagram profile link"
              rel="noreferrer"
            >
              {handle}
            </a>
          </div>
        );
      },
    },
    {
      accessorKey: "followersCount",
      header: "Followers & engagement",
      cell: ({ row }) => {
        const { engagementRate, platform, youtube } = row.original;

        if (platform === SocialPlatforms.youtube) {
          return (
            <div className="flex justify-between items-center gap-4 w-fit font-semibold lowercase tracking-wider">
              <Youtube />
              <div className="flex justify-start items-center gap-2 w-10">
                {millify(youtube?.subscriberCount ?? 0)}
              </div>
              <div className="border-border border-l h-5" />
              <div className="flex justify-start items-center gap-2 w-fit">
                <GrowthIcon />
                {youtube?.metrics?.engagementRate?.toFixed() || "-"}%
              </div>
            </div>
          );
        }

        // Instagram default
        return (
          <div className="flex justify-between items-center gap-4 w-fit font-semibold lowercase tracking-wider">
            <InstagramIcon className="w-5 h-5" />
            <div className="flex justify-start items-center gap-2 w-10">
              {millify(row.original.followersCount || 0)}
            </div>
            <div className="border-border border-l h-5" />
            <div className="flex justify-start items-center gap-2 w-fit">
              <GrowthIcon />
              {engagementRate?.toFixed()}%
            </div>
          </div>
        );
      },
    },

    {
      accessorKey: "creatorType",
      header: "Type of creator",
      cell: ({ row }) => {
        return (
          <div className="flex items-center gap-2 font-semibold capitalize tracking-wider">
            {row.getValue("creatorType") ?? "-"}
          </div>
        );
      },
    },
    {
      accessorKey: "fixedPayout",
      header: "Amount",
      cell: ({ row }) => {
        const { fixedPayout, id } = row.original;

        return (
          <div className="">
            {fixedPayout?.status === "active" ? (
              <div className="flex items-center gap-2 font-semibold text-secondary tracking-wider">
                ₹ {fixedPayout?.amount}
                <Link to={`/admin/fixed-payouts/edit/${id}`} className="">
                  <PenIcon />
                </Link>
              </div>
            ) : (
              "-"
            )}
          </div>
        );
      },
    },
    {
      accessorKey: "id",
      header: "",
      cell: ({ row: social }) => {
        return (
          <div className="flex justify-end items-center gap-4">
            <Link
              to={`/admin/fixed-payouts/${social.original.id}`}
              className=""
            >
              <Button className="bg-tertiarybuttonbg px-4 py-2 border border-border font-semibold">
                View details
              </Button>
            </Link>
            {social.original.fixedPayout?.status === "inactive" ||
            social.original.fixedPayout === null ? (
              <Link
                to={`/admin/fixed-payouts/edit/${social.original.id}`}
                className=""
              >
                <Button className="bg-approvebuttonbg px-4 py-2 border border-approvebuttonborder font-semibold text-approvebuttontext">
                  add to fixed payout
                </Button>
              </Link>
            ) : (
              <RemoveFromFixedPayout
                removeById={removeById}
                social={social.original}
              />
            )}
          </div>
        );
      },
    },
  ];

  const table = useReactTable({
    data,
    columns,
    onColumnFiltersChange: setColumnFilters,
    getCoreRowModel: getCoreRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    onColumnVisibilityChange: setColumnVisibility,
    onRowSelectionChange: setRowSelection,
    state: {
      columnFilters,
      columnVisibility,
      rowSelection,
    },
    manualPagination: true,
  });

  return (
    <div className="flex flex-col gap-16 px-10 pt-16">
      <div className="flex justify-between items-center">
        <h1 className="font-semibold text-2xl">Fixed payouts</h1>
        <div className="relative flex items-center bg-bginput py-2 pl-5 border border-border rounded-10">
          <SearchIcon />
          <Input
            value={search}
            onChange={(e) => {
              handleSearch(e);
            }}
            className="bg-transparent border-none w-96 font-semibold text-base placeholder:text-secondary"
            placeholder="Search by name, instagram ID, and number"
          />

          {search.trim().length > 0 && (
            <XIcon
              onClick={() => {
                setSearch("");
                setData([]);
                getSocials([], 1);
              }}
              className="right-5 absolute cursor-pointer"
            />
          )}
        </div>
      </div>

      {(data.length <= 0 && fetchingSocials) ||
      fetchingSocialsSearch ||
      typing ? (
        <div className="flex justify-center items-center pt-20 w-full">
          <Spinner />
        </div>
      ) : (
        <DataTableComponents
          table={table as ReturnType<typeof useReactTable>}
          columns={columns as ColumnDef<ISocial>[]}
          setPageNo={() => {
            setPageNo((prev) => {
              const nextPage = prev + 1;
              getSocials(data, nextPage);
              return nextPage;
            });
          }}
          sortBy={sortBy}
          setSortBy={(value) => {
            setData([]);
            setSortBy(value);
          }}
          loading={fetchingSocials}
          tableType="social"
          paginationData={paginationData}
        />
      )}
      {/* biome-ignore lint/a11y/useKeyWithClickEvents: <explanation> */}
      <div
        id="getFixedPayouts"
        className="hidden"
        onClick={() => {
          getSocials([], 1);
        }}
      />
    </div>
  );
};

export default FixedPayoutsAdminPartial;
